import Component from '@bythepixel/component-loader';
import { enableBodyScroll, disableBodyScroll } from '@Core/utility';
import { breakpoints, Durations } from '@Front/constants';
import { EVENT_NAMES as MobileMenuEvents } from '@Front/modules/navigation/components/navigationHeader/events';
import {
  selectors,
  settings,
  TOOLTIP_DELAY,
  FULLSCREEN_WIDGET_BREAKPOINT,
} from './config';
import { EVENT_NAMES } from './events';
import Schedule from './Schedule';
import Tooltip from './Tooltip';
import Trigger from './Trigger';
import KenectObserver from '../shared/KenectObserver';

export default class TextFeatureController extends Component {
  static selector = '[data-comp-kenect-text-controller]';

  tooltip;
  tooltipContainer;
  trigger;
  triggerContainer;
  widgetId;

  initialized = false;

  constructor(containerEl) {
    super(containerEl);

    this.widgetId = containerEl.dataset.widgetId;
    this.triggerContainer = containerEl.querySelector(
      selectors.trigger.container,
    );
    this.tooltipContainer = containerEl.querySelector(
      selectors.tooltip.container,
    );

    this.kenectObserver = new KenectObserver(document.body);
    this.trigger = new Trigger(this.triggerContainer);
    this.tooltip = new Tooltip(this.tooltipContainer);

    this.mount();
  }

  relocateKenectWidget() {
    this.kenectContainer.remove();
    this.$container.appendChild(this.kenectContainer);
  }

  showKenectWidget() {
    this.kenectContainer.ariaHidden = false;
  }

  hideKenectWidget() {
    this.kenectContainer.ariaHidden = true;
  }

  updateTooltipText() {
    if (this.tooltip.isClosed()) return;

    const { innerWidth } = window;
    const tooltipText = innerWidth >= breakpoints['break-2']
      ? settings.tooltipText.desktop
      : settings.tooltipText.mobile;

    this.tooltip.setText(tooltipText);
  }

  attachListeners() {
    window
      .matchMedia(`(min-width: ${breakpoints['break-2']}px)`)
      .addEventListener('change', () => this.updateTooltipText());

    window
      .matchMedia(`(min-width: ${FULLSCREEN_WIDGET_BREAKPOINT}px)`)
      .addEventListener('change', (e) => {
        if (!this.trigger.isOpen) return;

        // eslint-disable-next-line no-unused-expressions
        e.matches ? enableBodyScroll() : disableBodyScroll();
      });

    window.addEventListener(MobileMenuEvents.OPEN, () => {
      if (!this.initialized) return;

      this.trigger.close();
    });

    this.kenectObserver.observe(document.body, (currentMutation) => {
      const isKenectComponent = currentMutation.target.id.includes(
        this.widgetId,
      );

      if (!isKenectComponent || this.initialized) return;

      const kenectContainerSelector = `iframe-${this.widgetId}`;
      const kenectContainer = document.getElementById(kenectContainerSelector);
      const kenectButtonSelector = `button.reset-${this.widgetId}`;
      const kenectButton = document.querySelector(kenectButtonSelector);

      this.initialize(kenectContainer, kenectButton);
      this.kenectObserver.disconnect();
    });

    this.trigger.on(EVENT_NAMES.OPENED, () => {
      this.tooltip.disable();
      this.showKenectWidget();

      if (this.isFullscreenWidget()) {
        disableBodyScroll();
      }

      Schedule.queueNextTooltipAppearance(Durations.ONE_WEEK);
    });

    this.trigger.on(EVENT_NAMES.CLOSED, () => {
      this.hideKenectWidget();

      enableBodyScroll();
    });
  }

  isFullscreenWidget() {
    return window.innerWidth < FULLSCREEN_WIDGET_BREAKPOINT;
  }

  /**
   * Monitors window messages to determine whether the user has clicked the
   * close button within the native widget. Because we can't listen for clicks
   * within the widget, we can check for the 'CLOSE_IFRAME_<widget-id>' message
   * that gets sent when the close button is clicked.
   */
  listenForNativeWidgetCloseEvent() {
    const EXPECTED_MESSAGE_ORIGIN = 'https://storage.googleapis.com';
    const EXPECTED_MESSAGE_DATA_TYPE = `CLOSE_IFRAME_${this.widgetId}`;

    window.addEventListener('message', (e) => {
      const { origin, data } = e;

      const isExpectedOrigin = origin === EXPECTED_MESSAGE_ORIGIN;
      const isExpectedType = data.type === EXPECTED_MESSAGE_DATA_TYPE;

      if (!isExpectedOrigin || !isExpectedType) return;

      this.trigger.close();
    });
  }

  initialize(kenectContainerEl, kenectButtonEl) {
    this.kenectContainer = kenectContainerEl;
    this.relocateKenectWidget();
    this.trigger.setKenectButton(kenectButtonEl);
    this.trigger.enable();
    this.initialized = true;

    this.listenForNativeWidgetCloseEvent();

    setTimeout(() => {
      this.updateTooltipText();

      if (!Schedule.shouldShowTooltip()) return;

      this.tooltip.show();
    }, TOOLTIP_DELAY);
  }

  mount() {
    this.attachListeners();
  }
}
